import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import { getPublisherIdByPortalId } from 'utils/portalUtils';
import { LanguageDataParams } from 'views/Localization/components/types';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock
} from './api.utils';
import { useUserDetails } from './useUserDetails';

const useLocalization = () => {
  const axios = useAxios();
  const { publisherId, portalId } = useParams();
  const { userDetails } = useUserDetails();

  const effectivePublisherId = publisherId || (portalId ? getPublisherIdByPortalId(portalId, userDetails) : undefined);
  const publisherHeader = effectivePublisherId ? { 'x-publishers': effectivePublisherId } : undefined;

  const getAddedLanguagesList = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.LOCALIZATION_ADDED_LANGUAGES_LIST],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        {},
        publisherHeader
      );
    }
  });

  const addNewLanguage = useMutation(
    async (newLanguagesData: LanguageDataParams[]) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        { languages: newLanguagesData },
        publisherHeader
      );
      return response.data;
    },
    {}
  );

  const editLanguage = useMutation(
    async (newLanguagesData: LanguageDataParams[]) => {
      return await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        { languages: newLanguagesData },
        publisherHeader
      );
    },
    {}
  );

  const deleteLanguage = useMutation(
    async (languagesData: LanguageDataParams[]) => {
      return await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        { languages: languagesData },
        publisherHeader
      );
    },
    {}
  );

  const importStaticTranslationsCsvFile = useMutation(
    async (newFile: FormData) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/files`,
        newFile,
        publisherHeader
      );
      return response.data;
    }
  );

  const importDynamicTranslationsCsvFile = useMutation(
    async (newFile: FormData) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/dynamic-files`,
        newFile,
        publisherHeader
      );
      return response.data;
    }
  );

  const getStaticTranslationsCsvFile = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_STATIC_TRANSLATIONS_FILE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/files`,
        {},
        publisherHeader
      );
    },
    enabled: false,
    retry: false
  });

  const getDynamicTranslationsCsvFile = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_DYNAMIC_TRANSLATIONS_FILE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/dynamic-files`,
        {},
        publisherHeader
      );
    },
    enabled: false,
    retry: false
  });

  const getStaticTranslationsCsvLastUpdateDate = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_STATIC_TRANSLATIONS_FILE_LAST_UPDATE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/files/last-update`,
        {},
        publisherHeader
      );
    }
  });

  const getDynamicTranslationsCsvLastUpdateDate = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_DYNAMIC_TRANSLATIONS_FILE_LAST_UPDATE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/dynamic-files/last-update`,
        {},
        publisherHeader
      );
    }
  });

  return {
    getAddedLanguagesList,
    addNewLanguage,
    deleteLanguage,
    importStaticTranslationsCsvFile,
    importDynamicTranslationsCsvFile,
    getStaticTranslationsCsvFile,
    getDynamicTranslationsCsvFile,
    getStaticTranslationsCsvLastUpdateDate,
    getDynamicTranslationsCsvLastUpdateDate,
    editLanguage
  };
};

export default useLocalization;
